/**
 * OperationsTab - Vessel operations configuration tab
 */

import React from 'react';
import { Select } from '../../../catalyst/select';
import { Switch, Checkbox } from '../../../catalyst/checkbox';
import { Input } from '../../../catalyst/input';

const OperationsTab = ({ vesselConfig, updateConfig }) => {
  const handleOperationsToggle = (operation) => {
    const current = vesselConfig.operations || [];
    const updated = current.includes(operation)
      ? current.filter(o => o !== operation)
      : [...current, operation];
    updateConfig({ operations: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-medium text-gray-900">Operations Configuration</h3>
        <p className="mt-1 text-sm text-gray-600">Configure vessel operational capabilities</p>
      </div>

      {/* Operating Profile */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Operating Profile</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">International Voyages</p>
              <p className="text-sm text-gray-600">
                {vesselConfig.operatingStatus === 'inland' 
                  ? 'Not available for inland waterway vessels' 
                  : 'Voyages beyond UK waters'}
              </p>
            </div>
            <Switch
              checked={vesselConfig.internationalVoyages && vesselConfig.operatingStatus !== 'inland'}
              onChange={(checked) => {
                if (vesselConfig.operatingStatus === 'inland') {
                  // Don't allow international voyages for inland vessels
                  updateConfig({ internationalVoyages: false });
                } else {
                  updateConfig({ internationalVoyages: checked });
                }
              }}
              disabled={vesselConfig.operatingStatus === 'inland'}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Towing Capable</p>
              <p className="text-sm text-gray-600">Vessel equipped for towing operations</p>
            </div>
            <Switch
              checked={vesselConfig.towing_capable}
              onChange={(checked) => updateConfig({ towing_capable: checked })}
            />
          </div>
        </div>
      </div>

      {/* Special Operations */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Special Operations</h3>
        <div className="space-y-3">
          {[
            { value: 'towing_operations', label: 'Towing Operations', help: 'Commercial towing services' },
            { value: 'pilot_transfer', label: 'Pilot Transfer', help: 'Pilot boarding/landing operations' },
            { value: 'personnel_transfer', label: 'Personnel Transfer', help: 'Crew transfer operations' },
            { value: 'cargo_operations', label: 'Cargo Operations', help: 'Cargo handling and transport' },
            { value: 'survey_operations', label: 'Survey Operations', help: 'Hydrographic/seismic surveys' },
            { value: 'dive_support', label: 'Dive Support', help: 'Commercial diving operations' },
            { value: 'lifting', label: 'Lifting Operations', help: 'Crane and lifting equipment operations' },
            { value: 'fuel_transfer', label: 'Commercial Fuel Transfer', help: 'Ship-to-ship fuel transfer or commercial bunkering services (not routine shore fueling)' },
            { value: 'dangerous_goods', label: 'Dangerous Goods', help: 'DG cargo handling' },
            { value: 'patrol_operations', label: 'Patrol Operations', help: 'Security/enforcement patrols' },
            { value: 'passenger_operations', label: 'Passenger Operations', help: 'Commercial passenger service' },
            { value: 'workboat_operations', label: 'Workboat Operations', help: 'General workboat services' }
          ].map(op => (
            <label key={op.value} className="flex items-start gap-3">
              <Checkbox
                checked={vesselConfig.operations?.includes(op.value) || false}
                onChange={() => handleOperationsToggle(op.value)}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">{op.label}</p>
                <p className="text-sm text-gray-600">{op.help}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Dangerous Goods Configuration - Only show if dangerous_goods operation is selected */}
      {vesselConfig.operations?.includes('dangerous_goods') && (
        <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
          <h3 className="text-base font-medium text-red-900 mb-4">Dangerous Goods Configuration</h3>
          <p className="text-sm text-red-700 mb-4">Additional fire protection requirements apply when carrying dangerous goods</p>
          <div className="space-y-3">
            <label className="flex items-start gap-3">
              <Checkbox
                checked={vesselConfig.carriesClass1Explosives || false}
                onChange={(checked) => updateConfig({ carriesClass1Explosives: checked })}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">Class 1 Explosives</p>
                <p className="text-sm text-gray-600">Requires additional fire pumps per WBC3 29.8.1</p>
              </div>
            </label>

            <label className="flex items-start gap-3">
              <Checkbox
                checked={vesselConfig.dgNearAccommodation || false}
                onChange={(checked) => updateConfig({ dgNearAccommodation: checked })}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">DG Storage Near Accommodation</p>
                <p className="text-sm text-gray-600">Within 3m of fuel tanks, machinery or accommodation (requires A-60 insulation)</p>
              </div>
            </label>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700">DoC DG Certificate Number</label>
              <Input
                value={vesselConfig.docDgNumber || ''}
                onChange={(e) => updateConfig({ docDgNumber: e.target.value })}
                placeholder="Document of Compliance for Dangerous Goods number"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      )}

      {/* Environmental */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Environmental</h3>
        <div className="space-y-4">
          {/* Environmental Compliance Checkboxes */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Prohibited Discharge Area</p>
                <p className="text-sm text-gray-600">Operates in areas where sewage discharge is prohibited</p>
              </div>
              <Switch
                checked={vesselConfig.hasProhibitedDischargeArea || false}
                onChange={(checked) => updateConfig({ hasProhibitedDischargeArea: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">IMO Sewage System</p>
                <p className="text-sm text-gray-600">IMO approved sewage treatment system fitted</p>
              </div>
              <Switch
                checked={vesselConfig.hasIMOSewageSystem || false}
                onChange={(checked) => updateConfig({ hasIMOSewageSystem: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Unmanned Vessel (ROUV)</p>
                <p className="text-sm text-gray-600">Remotely operated unmanned vessel</p>
              </div>
              <Switch
                checked={vesselConfig.isUnmanned || false}
                onChange={(checked) => updateConfig({ isUnmanned: checked })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="oil-capacity" className="block text-sm font-medium text-gray-900">
              Oil Capacity (Litres)
            </label>
            <Input
              id="oil-capacity"
              type="number"
              value={vesselConfig.oilCapacity || ''}
              onChange={(e) => updateConfig({ oilCapacity: e.target.value })}
              placeholder="e.g., 200"
              className="mt-1"
            />
            <p className="mt-1 text-sm text-gray-600">Total oil capacity (fuel + lubricants)</p>
          </div>

        </div>
      </div>

      {/* SMS Configuration */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">SMS Configuration</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="sms-level" className="block text-sm font-medium text-gray-900">
              SMS Level
            </label>
            <Select
              value={vesselConfig.smsLevel || 'standard'}
              onChange={(value) => updateConfig({ smsLevel: value })}
              className="mt-1"
            >
              <option value="basic">Basic - Minimum WBC3 requirements only</option>
              <option value="standard">Standard - WBC3 + common best practices</option>
              <option value="comprehensive">Comprehensive - Full SMS with all risk assessments</option>
            </Select>
            <p className="mt-1 text-sm text-gray-600">
              Comprehensive level includes general operations risk assessments (mooring, maneuvering, anchoring, deck work, watch keeping)
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Include General Operations Risk Assessments</p>
              <p className="text-sm text-gray-600">
                Add risk assessments for routine operations (mooring, anchoring, deck work) - SMS best practice
              </p>
            </div>
            <Switch
              checked={vesselConfig.includeGeneralOperationsRA || vesselConfig.smsLevel === 'comprehensive'}
              onChange={(checked) => updateConfig({ includeGeneralOperationsRA: checked })}
              disabled={vesselConfig.smsLevel === 'comprehensive'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationsTab;